import { cloneElement, useCallback, useEffect, useState } from 'react';
import { ComPeriodo, acharCartao, semearGaleria } from '../../design/graficos/ComPeriodo';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import { ErroDeArea, EstadoVazio, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import {
  diaLongo,
  inteiro,
  lerDashboard,
  lerGaleria,
  lerInsightsDoAdmin,
  pctTexto,
  reais,
  tomDaVariacao,
} from '../../paineis';
import type {
  AtalhoDePeriodo,
  DashboardGerencial,
  FrescorDaFonte,
  GaleriaDeGraficos,
  Indicador,
  ItemDeAtencao,
  ModuloDaOperacao,
} from '../../paineis';
import { GradeDoDashboard, GraficoPrincipal } from './DestaquesDoDashboard';
import { SubnavDoPainel } from './SubnavDoPainel';
import {
  CartaoDoPainel,
  IconeExtra,
  ROTULO_DO_ATALHO,
  Sparkline,
  baixarCsv,
  diaCurto,
  periodoDaUrl,
  quando,
  reaisCompacto,
  textoDoPeriodo,
  trocarConsulta,
} from './pecasDoPainel';
import type { Celula } from './pecasDoPainel';
import './dashboard.css';

/**
 * TELA 11 — DASHBOARD GERENCIAL. "A Home mostra o agora. O Dashboard mostra o movimento."
 *
 * Período com comparação explícita, indicadores com semântica de variação (divergência subindo é
 * RUIM), frescor por fonte (cada uma com a sua hora) e saída para onde o número nasce. Indicador
 * sem fonte aparece como indisponível, com o motivo do servidor — nunca como zero.
 */

type Estado =
  | { fase: 'carregando' }
  | { fase: 'erro' }
  | { fase: 'sem-acesso' }
  | { fase: 'pronto'; d: DashboardGerencial };

const ATALHOS_DA_TELA: readonly AtalhoDePeriodo[] = ['hoje', 'ontem', '7d', '30d', 'mes'];
const CURTO: Record<AtalhoDePeriodo, string> = {
  hoje: 'Hoje',
  ontem: 'Ontem',
  '7d': '7d',
  '30d': '30d',
  mes: 'Mês',
};

const ICONE_DO_MODULO: Record<ModuloDaOperacao['modulo'], NomeDoIcone> = {
  giro: 'cycle',
  vendas: 'receipt',
  premiacao: 'money',
  pre_vencidos: 'clock',
  paradao: 'box',
};

const maiuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function Dashboard({ eu, parametros }: { eu: Eu | null; parametros: URLSearchParams }) {
  const { periodo, atalho } = periodoDaUrl(parametros, 'hoje');
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [nInsights, setNInsights] = useState<number | undefined>(undefined);
  const [galeria, setGaleria] = useState<GaleriaDeGraficos | null>(null);
  const [abrirPeriodo, setAbrirPeriodo] = useState(false);

  const carregar = useCallback(() => {
    let vivo = true;
    setEstado({ fase: 'carregando' });
    void lerDashboard(periodo).then((r) => {
      if (!vivo) return;
      if (r.ok) setEstado({ fase: 'pronto', d: r.dados });
      else setEstado({ fase: r.falha.tipo === 'sem-acesso' ? 'sem-acesso' : 'erro' });
    });
    // os gráficos (mesmo motor da seção Gráficos) vêm em paralelo, sem segurar o resto
    setGaleria(null);
    void lerGaleria(periodo).then((r) => {
      if (!vivo || !r.ok) return;
      semearGaleria(r.dados); // os cartões em "Página" usam esta mesma busca
      setGaleria(r.dados);
    });
    return () => {
      vivo = false;
    };
  }, [periodo.de, periodo.ate]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    let vivo = true;
    // o mesmo número da página Insights: cartões que pedem ação (atenção + oportunidade)
    void lerInsightsDoAdmin().then(
      (r) =>
        vivo &&
        r.ok &&
        setNInsights(
          r.dados.grupos
            .flatMap((g) => g.cartoes)
            .filter((c) => c.tom === 'alerta' || c.tom === 'oportunidade').length,
        ),
    );
    return () => {
      vivo = false;
    };
  }, []);

  if ((eu && !podeFazer(eu, 'painel.ver_completo')) || estado.fase === 'sem-acesso')
    return <SemAcesso area="O Dashboard" />;

  const d = estado.fase === 'pronto' ? estado.d : null;
  const vendas = d?.frescor.find((f) => f.fonte === 'vendas');
  const umDia = periodo.de === periodo.ate;
  const subtitulo = umDia ? maiuscula(diaLongo(periodo.de).replace(/ de \d{4}$/, '')) : null;

  const exportar = () => {
    if (d) exportarCsv(d);
  };

  return (
    <div className="db">
      <SubnavDoPainel
        atual="dashboard"
        insights={nInsights}
        direita={
          vendas?.ate ? (
            <span className="db-subnav-hora">
              <i className="db-ponto db-ok" />
              <span className="db-so-largo">Dados de vendas até </span>
              {quando(vendas.ate)}
            </span>
          ) : undefined
        }
      />
      <div className="db-corpo">
        <div className="db-topo">
          <div className="db-titulo">
            <h1>Dashboard</h1>
            <p>
              {subtitulo ?? textoDoPeriodo(periodo)}
              {d?.base ? ` · comparado com ${d.base}` : d ? ' · sem base de comparação' : ''}
            </p>
          </div>
          <div className="db-acoes">
            <div className="db-segmento" role="tablist" aria-label="Período">
              {ATALHOS_DA_TELA.map((a) => (
                <button
                  key={a}
                  type="button"
                  role="tab"
                  aria-selected={atalho === a}
                  className={`${atalho === a ? 'db-sel' : ''} db-seg-${a}`}
                  onClick={() => trocarConsulta(parametros, { p: a, de: null, ate: null })}
                >
                  <span className="db-so-largo">{ROTULO_DO_ATALHO[a]}</span>
                  <span className="db-so-curto">{CURTO[a]}</span>
                </button>
              ))}
            </div>
            <div className="db-periodo-caixa">
              <button
                type="button"
                className={`db-botao${atalho === 'personalizado' ? ' db-botao-ativo' : ''}`}
                aria-expanded={abrirPeriodo}
                onClick={() => setAbrirPeriodo((v) => !v)}
              >
                <Icone nome="cal" tamanho={15} traco={1.5} />
                {atalho === 'personalizado' ? (
                  textoDoPeriodo(periodo)
                ) : (
                  <>
                    <span className="db-so-largo">Personalizado</span>
                    <span className="db-so-curto">Período</span>
                  </>
                )}
              </button>
              {abrirPeriodo && (
                <PeriodoPersonalizado
                  de={periodo.de}
                  ate={periodo.ate}
                  aoFechar={() => setAbrirPeriodo(false)}
                  aoAplicar={(de, ate) => {
                    setAbrirPeriodo(false);
                    trocarConsulta(parametros, { p: null, de, ate });
                  }}
                />
              )}
            </div>
            <span className="db-divisor" />
            <a className="db-botao db-botao-tv" href="#/tv">
              <IconeExtra nome="tv" />
              <span className="db-so-largo">Modo TV</span>
              <span className="db-so-curto">TV</span>
            </a>
            <button type="button" className="db-botao" onClick={exportar} disabled={!d}>
              <Icone nome="down" tamanho={15} traco={1.6} />
              Exportar
            </button>
          </div>
        </div>

        {estado.fase === 'erro' ? (
          <ErroDeArea oQue="o dashboard" aoTentar={carregar} />
        ) : !d ? (
          <Esqueleto />
        ) : (
          <>
            <FaixaDeFrescor frescor={d.frescor} aoAtualizar={carregar} />
            <ResumoDoCelular d={d} umDia={umDia} />
            <Indicadores d={d} />
            <div className="db-meio">
              {galeria ? (
                <ComPeriodo id="dashboard:principal" padrao={periodo}>
                  {(g, seletor) =>
                    g ? (
                      cloneElement(GraficoPrincipal({ g }), { periodo: seletor })
                    ) : (
                      <div className="gx-cartao gx-carregando">{seletor}Carregando…</div>
                    )
                  }
                </ComPeriodo>
              ) : (
                <div className="gx-vazio" style={{ minHeight: 360 }}>
                  Carregando os gráficos…
                </div>
              )}
              <Atencao itens={d.atencao} insights={nInsights} />
            </div>
            {galeria && (
              <div className="gx-grade db-grade-graficos">
                {['db-meta', 'db-programas', 'db-equipe', 'db-quando'].map((id) => (
                  <ComPeriodo key={id} id={`dashboard:${id}`} padrao={periodo}>
                    {(g, seletor) => {
                      const c = g ? acharCartao(GradeDoDashboard({ g }), id) : null;
                      return c ? (
                        cloneElement(c, { periodo: seletor })
                      ) : (
                        <div className="gx-cartao gx-carregando">{seletor}Carregando…</div>
                      );
                    }}
                  </ComPeriodo>
                ))}
              </div>
            )}
            <div className="db-baixo">
              <Operacao modulos={d.operacao} />
              <TopDaSemana top={d.top} />
            </div>
            <div className="db-atalhos-celular">
              <a className="db-atalho" href="#/graficos">
                <IconeExtra nome="chart" tamanho={17} />
                Gráficos
              </a>
              <a className="db-atalho" href="#/insights">
                <IconeExtra nome="bulb" tamanho={17} />
                Insights{nInsights !== undefined ? ` · ${String(nInsights)}` : ''}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function PeriodoPersonalizado({
  de,
  ate,
  aoFechar,
  aoAplicar,
}: {
  de: string;
  ate: string;
  aoFechar: () => void;
  aoAplicar: (de: string, ate: string) => void;
}) {
  const [a, setA] = useState(de);
  const [b, setB] = useState(ate);
  const invalido = !a || !b || a > b;
  return (
    <div className="db-pop" role="dialog" aria-label="Período personalizado">
      <label>
        De
        <input type="date" value={a} max={b || undefined} onChange={(e) => setA(e.target.value)} />
      </label>
      <label>
        Até
        <input type="date" value={b} min={a || undefined} onChange={(e) => setB(e.target.value)} />
      </label>
      {a > b && <p className="db-pop-erro">O início vem depois do fim.</p>}
      <div className="db-pop-acoes">
        <button type="button" className="db-botao" onClick={aoFechar}>
          Cancelar
        </button>
        <button
          type="button"
          className="db-botao db-botao-primario"
          disabled={invalido}
          onClick={() => aoAplicar(a, b)}
        >
          Aplicar
        </button>
      </div>
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="db-esqueleto" aria-busy="true" aria-label="Carregando o dashboard">
      <div className="db-esq-kpis">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="db-esq-kpi">
            <i className="db-esq-linha" style={{ width: '70%' }} />
            <i className="db-esq-bloco" style={{ width: '85%' }} />
          </div>
        ))}
      </div>
      <div className="db-esq-grafico">
        <i className="db-esq-linha" style={{ width: 130 }} />
        <i className="db-esq-area" />
      </div>
    </div>
  );
}

/* ---------------------------- frescor ---------------------------- */

const CURTO_DA_FONTE: Record<FrescorDaFonte['fonte'], string> = {
  vendas: 'Vendas',
  giro: 'Giro',
  relatorio_hos: 'HOS',
  estoque: 'Estoque',
};

function FaixaDeFrescor({
  frescor,
  aoAtualizar,
}: {
  frescor: readonly FrescorDaFonte[];
  aoAtualizar: () => void;
}) {
  return (
    <div className="db-frescor">
      <span className="db-frescor-rotulo">
        <span className="db-so-largo">FRESCOR DOS DADOS</span>
        <span className="db-so-curto">DADOS</span>
      </span>
      {frescor.map((f) => (
        <span
          key={f.fonte}
          className={`db-fonte db-fonte-${f.estado}`}
          title={`${f.rotulo}: ${f.detalhe}`}
        >
          <i
            className={`db-ponto db-${f.estado === 'ok' ? 'ok' : f.estado === 'antigo' ? 'antigo' : 'off'}`}
          />
          <span className="db-so-largo">
            {f.rotulo} · {f.ate && f.estado !== 'indisponivel' ? quando(f.ate) : f.detalhe}
          </span>
          <span className="db-so-curto">
            {CURTO_DA_FONTE[f.fonte]}{' '}
            {f.ate && f.estado !== 'indisponivel' ? quando(f.ate) : 'indisponível'}
          </span>
        </span>
      ))}
      <span className="db-flex" />
      <button type="button" className="db-link-botao" onClick={aoAtualizar}>
        <Icone nome="retry" tamanho={14} traco={1.6} />
        <span className="db-so-largo">Atualizar agora</span>
        <span className="db-so-curto">Atualizar</span>
      </button>
    </div>
  );
}

/* --------------------------- indicadores -------------------------- */

function Indicadores({ d }: { d: DashboardGerencial }) {
  const i = d.indicadores;
  return (
    <div className="db-kpis">
      <Kpi rotulo="ITENS VENDIDOS" curto="ITENS" i={i.itens} formato={inteiro} />
      <Kpi rotulo="FATURAMENTO" i={i.faturamento} formato={reaisCompacto} />
      <Kpi rotulo="TICKET MÉDIO" i={i.ticket} formato={(v) => reais(v)} />
      <Kpi
        rotulo="PREMIAÇÃO GERADA"
        curto="PREMIAÇÃO"
        i={i.premiacao}
        formato={reaisCompacto}
        classe="db-kpi-premio"
      />
      <Kpi rotulo="GIRO" i={i.giro} formato={(v) => `${String(v)}%`} barra />
      <Kpi rotulo="EQUIPE ATIVA" i={i.equipe} formato={inteiro} classe="db-kpi-equipe" />
      <Kpi rotulo="DIVERGÊNCIAS" i={i.divergencias} formato={inteiro} />
    </div>
  );
}

function Variacao({ i }: { i: Indicador }) {
  if (!i.disponivel) return null;
  if (!i.variacao)
    return i.sentido === 'neutro' ? null : (
      <span className="db-var db-var-sem">sem base comparável</span>
    );
  const tom = tomDaVariacao(i);
  const subiu = i.variacao.pct > 0;
  return (
    <span className={`db-var db-var-${tom}`} title={`contra ${i.variacao.base}`}>
      {i.variacao.pct !== 0 && (
        <IconeExtra nome={subiu ? 'up-t' : 'down-t'} tamanho={12} traco={2.1} />
      )}
      {pctTexto(i.variacao.pct)}
      <span className="db-var-base"> vs {i.variacao.base}</span>
    </span>
  );
}

function Kpi({
  rotulo,
  curto,
  i,
  formato,
  barra,
  classe,
}: {
  rotulo: string;
  curto?: string;
  i: Indicador;
  formato: (v: number) => string;
  barra?: boolean;
  classe?: string;
}) {
  const r = (
    <div className="db-kpi-rotulo">
      <span className="db-so-largo">{rotulo}</span>
      <span className="db-so-curto">{curto ?? rotulo}</span>
    </div>
  );
  if (!i.disponivel)
    return (
      <a className={`db-kpi db-kpi-off ${classe ?? ''}`} href={i.link} title={i.porque}>
        {r}
        <div className="db-kpi-valor db-kpi-nulo">—</div>
        <div className="db-kpi-porque">indisponível · {i.porque}</div>
      </a>
    );
  const tom = tomDaVariacao(i);
  return (
    <a className={`db-kpi ${classe ?? ''}`} href={i.link} title={`Fonte: ${i.fonte}`}>
      {r}
      <div className="db-kpi-valor">{formato(i.valor)}</div>
      {i.detalhe && (
        <div className="db-kpi-detalhe" title={i.detalhe}>
          {i.detalhe}
        </div>
      )}
      {barra && (
        <div className="db-kpi-barra">
          <i style={{ width: `${String(Math.min(100, Math.max(0, i.valor)))}%` }} />
        </div>
      )}
      <div className="db-kpi-pe">
        <Variacao i={i} />
        {i.serie && <Sparkline serie={i.serie} tom={tom === 'ruim' ? 'ruim' : 'normal'} />}
      </div>
    </a>
  );
}

/* ------------------------ resumo do celular ----------------------- */

function ResumoDoCelular({ d, umDia }: { d: DashboardGerencial; umDia: boolean }) {
  const f = d.indicadores.faturamento;
  const mini = (rotulo: string, i: Indicador, fmt: (v: number) => string, cls?: string) => (
    <div>
      <span>{rotulo}</span>
      <strong className={i.disponivel ? cls : 'db-rc-nulo'}>
        {i.disponivel ? fmt(i.valor) : '—'}
      </strong>
    </div>
  );
  return (
    <div className="db-resumo-celular">
      <div className="db-rc-rotulo">FATURAMENTO {umDia ? 'NO DIA' : 'NO PERÍODO'}</div>
      {f.disponivel ? (
        <div className="db-rc-valor">
          {reaisCompacto(f.valor)}
          {f.variacao && (
            <span className={`db-rc-var db-rc-var-${tomDaVariacao(f)}`}>
              <IconeExtra nome={f.variacao.pct >= 0 ? 'up-t' : 'down-t'} tamanho={12} traco={2.1} />
              {pctTexto(f.variacao.pct)}
            </span>
          )}
        </div>
      ) : (
        <div className="db-rc-porque">indisponível · {f.porque}</div>
      )}
      <div className="db-rc-mini">
        {mini('Itens', d.indicadores.itens, inteiro)}
        {mini('Ticket', d.indicadores.ticket, (v) => reais(v), 'db-rc-ticket')}
        {mini('Premiação', d.indicadores.premiacao, reaisCompacto, 'db-rc-premio')}
        {mini('Giro', d.indicadores.giro, (v) => `${String(v)}%`)}
      </div>
      <div className="db-rc-fontes">
        {d.frescor
          .map(
            (x) =>
              `${CURTO_DA_FONTE[x.fonte]} ${x.ate && x.estado !== 'indisponivel' ? quando(x.ate) : 'indisponível'}`,
          )
          .join(' · ')}
      </div>
    </div>
  );
}

/* ------------------------- por hora / dia ------------------------- */

function iconeDaAtencao(a: ItemDeAtencao): NomeDoIcone {
  return /bloco|responsavel|giro|atrasad|ausente/i.test(a.tipo) ? 'clock' : 'warn';
}

function Atencao({
  itens,
  insights,
}: {
  itens: readonly ItemDeAtencao[];
  insights?: number | undefined;
}) {
  const LIMITE_LARGO = 4;
  const LIMITE_CURTO = 2;
  const sobra = Math.max(0, itens.length - LIMITE_CURTO);
  return (
    <CartaoDoPainel
      className="db-atencao"
      titulo="Precisa de atenção"
      direita={<span className="db-bola">{itens.length}</span>}
    >
      {itens.length === 0 ? (
        <EstadoVazio
          icone="check"
          titulo="Nada pedindo atenção agora"
          texto="Divergências, Giro, validades, cadastros e bloqueios estão em dia."
        />
      ) : (
        <ul className="db-atencao-lista">
          {itens.slice(0, LIMITE_LARGO).map((a, k) => (
            <li
              key={`${a.tipo}-${String(k)}`}
              className={`db-sev-${a.severidade}${k >= LIMITE_CURTO ? ' db-so-largo-bloco' : ''}`}
            >
              <a href={a.link}>
                <Icone nome={iconeDaAtencao(a)} tamanho={17} traco={1.6} />
                <div>
                  <strong>{a.titulo}</strong>
                  {a.detalhe && <span className="db-atencao-detalhe">{a.detalhe}</span>}
                  <span className="db-atencao-acao">{a.acao}</span>
                </div>
                <span className="db-atencao-seta">
                  <Icone nome="chev-r" tamanho={15} traco={1.6} />
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
      {(sobra > 0 || (insights ?? 0) > 0) && (
        <a className="db-atencao-pe" href="#/insights">
          <IconeExtra nome="bulb" tamanho={14} />
          <span>
            {sobra > 0 && (
              <span className="db-so-curto-inline">
                +{sobra} pendência{sobra > 1 ? 's' : ''}
                {(insights ?? 0) > 0 ? ' · ' : ''}
              </span>
            )}
            {(insights ?? 0) > 0 && `${String(insights)} insight${insights === 1 ? '' : 's'}`}
          </span>
          <b>Ver</b>
        </a>
      )}
    </CartaoDoPainel>
  );
}

/* --------------------------- operação --------------------------- */

function Operacao({ modulos }: { modulos: readonly ModuloDaOperacao[] }) {
  return (
    <CartaoDoPainel
      className="db-operacao"
      titulo={
        <>
          <span className="db-so-largo">Operação por módulo</span>
          <span className="db-so-curto">Operação</span>
        </>
      }
      direita={<span className="db-cinza">resumo · o detalhe fica em cada área</span>}
    >
      {modulos.length === 0 ? (
        <EstadoVazio icone="grid" titulo="Nenhum módulo com dado no período" />
      ) : (
        <ul className="db-modulos">
          {modulos.map((m) => (
            <li key={m.modulo} className={`db-modulo-${m.modulo}`}>
              <a href={m.link}>
                <span className="db-modulo-icone">
                  <Icone nome={ICONE_DO_MODULO[m.modulo]} tamanho={17} traco={1.5} />
                </span>
                <span className="db-modulo-nome">
                  <strong>{m.titulo}</strong>
                  <span>{m.subtitulo}</span>
                </span>
                {m.progresso !== null ? (
                  <span className="db-modulo-progresso">
                    <span className="db-modulo-trilho">
                      <i style={{ width: `${String(m.progresso)}%` }} />
                    </span>
                    <b>{m.progresso}%</b>
                  </span>
                ) : null}
                <span
                  className={`db-modulo-resumo${m.progresso !== null ? ' db-modulo-resumo-alt' : ''}`}
                >
                  {m.resumo}
                </span>
                <span className="db-selos">
                  {m.selos.map((s) => (
                    <span key={s.texto} className={`db-selo db-selo-${s.tom}`}>
                      {s.texto}
                    </span>
                  ))}
                </span>
                <span className="db-modulo-acao">{m.acao}</span>
                <span className="db-modulo-seta">
                  <Icone nome="chev-r" tamanho={15} traco={1.6} />
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </CartaoDoPainel>
  );
}

/* ---------------------------- top 5 ---------------------------- */

function TopDaSemana({ top }: { top: DashboardGerencial['top'] }) {
  return (
    <CartaoDoPainel
      className="db-top"
      titulo="Top 5 da semana"
      direita={
        <a className="db-link" href="#/ranking">
          Ranking completo
        </a>
      }
    >
      {top.linhas.length === 0 ? (
        <EstadoVazio
          icone="trophy"
          titulo="Ninguém lançou itens nesta semana"
          texto={`Semana ${diaCurto(top.de)}–${diaCurto(top.ate)}.`}
        />
      ) : (
        <ol className="db-top-lista">
          {top.linhas.map((l) => (
            <li key={l.accountId}>
              <span className={`db-pos db-pos-${String(l.posicao)}`}>{l.posicao}</span>
              <span className="db-top-nome">{l.nome}</span>
              <span className="db-top-itens">{inteiro(l.itens)} itens</span>
            </li>
          ))}
        </ol>
      )}
      <div className="db-top-pe">
        <span>
          Semana {diaCurto(top.de)}–{diaCurto(top.ate)}
        </span>
        {top.metaSemanal > 0 && <b>meta individual: {inteiro(top.metaSemanal)} itens</b>}
      </div>
    </CartaoDoPainel>
  );
}

/* ---------------------------- exportar ---------------------------- */

function exportarCsv(d: DashboardGerencial) {
  const i = d.indicadores;
  const linha = (nome: string, x: Indicador): Celula[] =>
    x.disponivel
      ? [nome, x.valor, x.detalhe, x.variacao?.pct ?? null, x.variacao?.base ?? null, x.fonte, null]
      : [nome, null, null, null, null, null, x.porque];
  const linhas: Celula[][] = [
    ['Dashboard gerencial', `${d.periodo.de} a ${d.periodo.ate}`],
    [
      'Gerado em',
      new Date(d.atualizadoEm).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
    ],
    ['Base de comparação', d.base ?? 'sem base'],
    [],
    ['Fonte', 'Estado', 'Dados até', 'Detalhe'],
    ...d.frescor.map((f): Celula[] => [f.rotulo, f.estado, f.ate, f.detalhe]),
    [],
    [
      'Indicador',
      'Valor',
      'Detalhe',
      'Variação %',
      'Comparado com',
      'Fonte',
      'Indisponível porque',
    ],
    linha('Itens vendidos', i.itens),
    linha('Faturamento (R$)', i.faturamento),
    linha('Ticket médio (R$)', i.ticket),
    linha('Premiação gerada (R$)', i.premiacao),
    linha('Giro (%)', i.giro),
    linha('Equipe ativa', i.equipe),
    linha('Divergências', i.divergencias),
    [],
    ['Dia', 'Faturamento (R$)', 'Itens', 'Referência do faturamento (R$)', 'Fonte'],
    ...d.porDia.map((x): Celula[] => [x.dia, x.faturamento, x.itens, x.base, 'Central de Vendas']),
    [],
    [
      'Hora',
      d.porHora.metrica === 'itens' ? 'Itens (média por dia)' : 'Faturamento (R$, média por dia)',
      `Base (${d.porHora.base ?? 'sem base'})`,
      'Fonte',
    ],
    ...d.porHora.horas.map((x): Celula[] => [
      `${String(x.hora).padStart(2, '0')}h`,
      x.atual,
      x.base,
      d.porHora.fonte === 'relatorio_hos' ? 'vendas do HOS' : 'lançamentos do InovaPay',
    ]),
  ];
  baixarCsv(`dashboard-${d.periodo.de}-a-${d.periodo.ate}.csv`, linhas);
}
